import { useEffect, useState } from 'react';
import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { Layout } from '../components/Layout';
import { CurrentPlanCard } from './subscription/CurrentPlanCard';
import { PlanCard } from './subscription/PlanCard';
import { FAQSection } from './subscription/FAQSection';
import { StripePaymentForm } from '../components/StripePaymentModal';
import { PaymentProcessing } from '../components/PaymentProcessing';
import { subscriptionService, type SubscriptionData } from '../services/subscription.service';
import type { SubscriptionTier } from './subscription/constants';
import styles from './SubscriptionPage.module.css';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

export function SubscriptionPage() {
  const { isAuthenticated, isLoading, getToken } = useKindeAuth();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/');
      return;
    }

    if (isAuthenticated) {
      fetchSubscription();
    }
  }, [isAuthenticated, isLoading, navigate]);

  const fetchSubscription = async () => {
    try {
      const accessToken = await getToken();
      if (!accessToken) {
        throw new Error('No access token available');
      }
      const data = await subscriptionService.getSubscription(accessToken);
      setSubscription(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgrade = async () => {
    try {
      const accessToken = await getToken();
      if (!accessToken) {
        throw new Error('No access token available');
      }

      // Replace with your actual Stripe price ID from the dashboard
      const STRIPE_PRICE_ID = import.meta.env.VITE_STRIPE_PRICE_ID || '';
      if (!STRIPE_PRICE_ID) {
        throw new Error('Stripe price ID not configured');
      }

      const { clientSecret } = await subscriptionService.createSubscription(
        accessToken,
        STRIPE_PRICE_ID
      );

      setClientSecret(clientSecret);
      setShowPaymentForm(true);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to start upgrade');
    }
  };

  const handlePaymentSuccess = () => {
    setShowPaymentForm(false);
    setClientSecret(null);
    setIsProcessingPayment(true);
  };

  const handlePaymentProcessingComplete = (updatedSubscription: SubscriptionData) => {
    setSubscription(updatedSubscription);
    setIsProcessingPayment(false);
  };

  const handlePaymentProcessingTimeout = () => {
    setIsProcessingPayment(false);
    setError('Payment successful but subscription update is taking longer than expected. Please refresh the page in a moment.');
  };

  const handleCancelPayment = () => {
    setShowPaymentForm(false);
    setClientSecret(null);
  };

  const handleManageSubscription = async () => {
    try {
      const accessToken = await getToken();
      if (!accessToken) {
        throw new Error('No access token available');
      }

      const { url } = await subscriptionService.createPortalSession(
        accessToken,
        window.location.href
      );

      window.location.href = url;
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to open portal');
    }
  };

  if (isLoading || loading) {
    return (
      <Layout>
        <div style={{ padding: '80px 24px', minHeight: 'calc(100vh - 200px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ fontSize: '1.25rem' }}>Loading...</div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div style={{ padding: '80px 24px', minHeight: 'calc(100vh - 200px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ fontSize: '1.25rem', color: '#dc2626' }}>Error: {error}</div>
        </div>
      </Layout>
    );
  }

  if (!subscription) {
    return null;
  }

  const currentTier = subscription.tier as SubscriptionTier;

  return (
    <Layout>
      <div className={styles.container}>
        <div className={styles.content}>
          <h1 className={styles.title}>
            Subscription Plans
          </h1>

          <CurrentPlanCard
            tier={subscription.tier}
            dailyLimit={subscription.dailyLimit}
            isExpired={subscription.isExpired}
            endDate={subscription.endDate}
          />

          <div className={styles.plansGrid}>
            <PlanCard
              tier="Free"
              isCurrentPlan={currentTier === 'Free'}
            />
            <PlanCard
              tier="Standard"
              isCurrentPlan={currentTier === 'Standard'}
              onUpgrade={handleUpgrade}
              onManage={handleManageSubscription}
            />
          </div>

          {showPaymentForm && clientSecret && (
            <Elements stripe={stripePromise} options={{ clientSecret }}>
              <StripePaymentForm
                onCancel={handleCancelPayment}
                onSuccess={handlePaymentSuccess}
                tier="Standard"
              />
            </Elements>
          )}

          {isProcessingPayment && (
            <PaymentProcessing
              getToken={getToken}
              onComplete={handlePaymentProcessingComplete}
              onTimeout={handlePaymentProcessingTimeout}
            />
          )}

          <FAQSection />
        </div>
      </div>
    </Layout>
  );
}